/**
 * What to do when a match reports where it has got to.
 *
 * **One place decides, because the bug was a missing second place.** The
 * screen learns a match has finished in two ways: the repeating check while
 * it waits, and the first look straight after sending. Only the first of
 * those fetched the conversation, and it only runs while something is still
 * going.
 *
 * A full match takes about half a minute, so the first look says "matching"
 * and the repeating check starts. Asking what else was close is answered
 * from words the match already wrote, with no trip to the AI at all, so it
 * is finished before that first look comes back: the repeating check never
 * starts, nothing fetches the conversation, and the question sits on screen
 * with no answer under it until the tab is left and reopened.
 *
 * Writing the rule here rather than at each place is what stops a third
 * place being added without it.
 */
import type { MatchProgress } from '../types'

/** What the screen does with a report. */
export interface Watcher {
  /** Keep what it says, so the waiting text and the stop button follow it. */
  keep: (progress: MatchProgress) => void
  /** Fetch the conversation, because the answer is on it now. */
  pull: () => void
}

/**
 * Whether there is an answer to go and get.
 *
 * Only a finished match has written a turn. A failed or stopped one has
 * written nothing, and what the person needs to see comes from the report
 * itself rather than from the conversation.
 */
export const hasAnswer = (state: MatchProgress['state']): boolean => state === 'done'

/** Takes one report, however the screen came by it. */
export function onProgress(progress: MatchProgress, watcher: Watcher): void {
  watcher.keep(progress)
  if (hasAnswer(progress.state)) watcher.pull()
}

/**
 * How far along the bar is, out of a hundred.
 *
 * Moved by what has actually happened rather than by a guessed percentage.
 * For one job that is the stage. For a paste of several jobs it is also how
 * many of them are finished, so a match of five that is on its fourth does
 * not sit where it sat on its first and look stuck.
 */
export function howFar(progress: Pick<MatchProgress, 'state' | 'jobs'>): number {
  const byState = {
    queued: 10, reading: 35, matching: 70, done: 100, failed: 100, stopped: 100,
  }[progress.state] ?? 10
  const jobs = progress.jobs
  if (!jobs || jobs.total < 2 || progress.state !== 'matching') return byState
  return Math.round(35 + (60 * Math.min(jobs.done, jobs.total)) / jobs.total)
}
